import React, { memo, useState } from 'react';
import { Button, Card, Form, Input, Popover, Select } from 'antd';
import { ColorResult, TwitterPicker } from 'react-color';
import { FontColorsOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { If, SubmitButton } from '@/components';
import snakeCase from 'lodash/snakeCase';
import { CtaDataInput, CtaPresetType } from '@/graphql/generated/graphql';
import { ObjKeyT } from '@/types';
import { CardTabListType } from 'antd/lib/card/Card';
import { useReactiveVar } from '@apollo/client';
import { currentUserVar } from '@/graphql/operations/user';

const { Item } = Form;

type Props = {
  onAddCTA: (data: CtaDataInput) => void;
  renderOptions?: Record<'text' | 'link' | 'color', boolean>;
};

type FormItemsT = {
  text: string;
  link?: string;
  preset?: CtaPresetType;
  color: string;
};

const presetLinks = [
  {
    label: 'New User Preferences',
    value: CtaPresetType.NewUser,
  },
];

let cardTabs: CardTabListType[] = [
  {
    key: 'custom',
    tab: 'Custom',
  },
  {
    key: 'presets',
    tab: 'Presets',
  },
];

const CTASelect = memo<Props>(({ onAddCTA, renderOptions = { text: true, link: true, color: true } }) => {
  const [form] = Form.useForm<FormItemsT>();
  const [color, setColor] = useState('var(--arya-primary-color)');
  const [activeTab, setActiveTab] = useState('custom');
  const [ctaPopupVisible, setCtaPopupVisible] = useState(false);
  const currentUser = useReactiveVar(currentUserVar);

  if (currentUser.organization.id === '4e2c778d-6fc3-4ad0-a23d-2092a436b64a') {
    cardTabs = [
      {
        key: 'custom',
        tab: 'Custom',
      },
    ];
  }

  const onAddCTAData = (values: FormItemsT) => {
    const link =
      values.link?.startsWith('http') || values.link?.startsWith('https') ? values.link : `https://${values.link}`;
    onAddCTA({
      variable: `~~${snakeCase(values.text)}~~`,
      text: values.text,
      link,
      color: color === 'var(--arya-primary-color)' ? 'black' : color,
      presetType: values.preset,
    });
    setCtaPopupVisible(false);
  };

  const onSelectColor = (value: ColorResult) => {
    setColor(value.hex);
  };

  const onTabChange = (tab: string) => {
    form.resetFields();
    setActiveTab(tab);
  };

  const ctaForm = (name: 'custom' | 'presets') => {
    return (
      <Form
        form={form}
        onFinish={onAddCTAData}
        initialValues={{
          color: 'var(--arya-primary-color)',
          preset: CtaPresetType.NewUser,
        }}
      >
        <If condition={name === 'presets'}>
          <Item name="preset" label="CTA Link" required>
            <Select>
              {presetLinks.map((link) => (
                <Select.Option key={link.value} value={link.value}>
                  {link.label}
                </Select.Option>
              ))}
            </Select>
          </Item>
        </If>
        <If condition={!!renderOptions?.text}>
          <Item required name="text" label="CTA Text">
            <Input placeholder="ex: My Earnings" />
          </Item>
        </If>
        <If condition={!!renderOptions?.link && name === 'custom'}>
          <Item required name="link" label="CTA Link">
            <Input placeholder="ex: https://google.com" />
          </Item>
        </If>
        <If condition={!!renderOptions?.color}>
          <Item name="color" label="CTA Color">
            <Popover trigger="click" placement="bottom" content={<TwitterPicker onChange={onSelectColor} />}>
              <Button icon={<FontColorsOutlined style={{ color: color }} />} />
            </Popover>
          </Item>
        </If>
        <Item>
          <SubmitButton size="small" type="primary" name="Insert CTA" />
        </Item>
      </Form>
    );
  };

  const contentList: ObjKeyT<React.ReactNode> = {
    custom: ctaForm('custom'),
    presets: ctaForm('presets'),
  };

  const ctaPopoverTabs = () => {
    return (
      <Card tabList={cardTabs} activeTabKey={activeTab} onTabChange={onTabChange} variant="borderless">
        {contentList[activeTab]}
      </Card>
    );
  };

  return (
    <Popover
      content={ctaPopoverTabs}
      onOpenChange={setCtaPopupVisible}
      open={ctaPopupVisible}
      trigger="click"
      placement="bottom"
    >
      <Button type="primary">
        Add CTA <PlusCircleOutlined />
      </Button>
    </Popover>
  );
});

export default CTASelect;
